// Appearance: five dark glass presets, a contrast step, a motion switch and
// a text size.
// The tokens live in styles/globals.css under html[data-theme=…]; this file
// only names the presets, persists the choice and applies it to <html>.

export const THEMES = [
  {
    id: "graphite",
    name: "Graphite",
    description: "Warm neutral, teal accent",
    // Preview swatches only: canvas, card, accent, glow
    swatch: ["#0d0d0c", "#1c1b1a", "#2a7f7a", "#37918c"],
    themeColor: "#0d0d0c",
  },
  {
    id: "navy",
    name: "Navy",
    description: "Deep blue, teal accent",
    swatch: ["#080f19", "#141e2b", "#2a7f7a", "#2b8fd1"],
    themeColor: "#080f19",
  },
  {
    id: "obsidian",
    name: "Obsidian",
    description: "True black, highest contrast",
    swatch: ["#050505", "#121212", "#f5f5f5", "#3a3a3a"],
    themeColor: "#050505",
  },
  {
    id: "dusk",
    name: "Dusk",
    description: "Slate violet, lavender accent",
    swatch: ["#0e0d12", "#1b1a20", "#5f3fc4", "#e2634a"],
    themeColor: "#0e0d12",
  },
  {
    id: "ember",
    name: "Ember",
    description: "Warm brown, amber accent",
    swatch: ["#0f0c0a", "#1f1a17", "#e8a83a", "#d9663a"],
    themeColor: "#0f0c0a",
  },
] as const;

export type ThemeId = (typeof THEMES)[number]["id"];

// Text size scales the root font size; every size in the app is in rem, so
// type, spacing and controls grow together. Scales live in globals.css under
// html[data-text-size=…].
export const TEXT_SIZES = [
  { id: "small", name: "Small" },
  { id: "default", name: "Default" },
  { id: "large", name: "Large" },
  { id: "xlarge", name: "Extra large" },
] as const;

export type TextSize = (typeof TEXT_SIZES)[number]["id"];
export type Contrast = "standard" | "high";
export type Motion = "full" | "reduced";

export interface Appearance {
  theme: ThemeId;
  contrast: Contrast;
  motion: Motion;
  textSize: TextSize;
}

export const DEFAULT_APPEARANCE: Appearance = {
  theme: "graphite",
  contrast: "standard",
  motion: "full",
  textSize: "default",
};

export const APPEARANCE_KEY = "fexr-appearance";
const CHANGE_EVENT = "fexr-appearance-change";

const isTheme = (v: unknown): v is ThemeId => THEMES.some((t) => t.id === v);
const isTextSize = (v: unknown): v is TextSize =>
  TEXT_SIZES.some((t) => t.id === v);

export function readAppearance(): Appearance {
  if (typeof window === "undefined") return DEFAULT_APPEARANCE;
  try {
    const raw = JSON.parse(localStorage.getItem(APPEARANCE_KEY) || "{}");
    return {
      theme: isTheme(raw.theme) ? raw.theme : DEFAULT_APPEARANCE.theme,
      contrast: raw.contrast === "high" ? "high" : "standard",
      motion: raw.motion === "reduced" ? "reduced" : "full",
      textSize: isTextSize(raw.textSize)
        ? raw.textSize
        : DEFAULT_APPEARANCE.textSize,
    };
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

export function applyAppearance(a: Appearance) {
  const root = document.documentElement;
  root.dataset.theme = a.theme;
  if (a.contrast === "high") root.dataset.contrast = "high";
  else delete root.dataset.contrast;
  if (a.motion === "reduced") root.dataset.motion = "reduced";
  else delete root.dataset.motion;
  if (a.textSize !== "default") root.dataset.textSize = a.textSize;
  else delete root.dataset.textSize;
  const color = THEMES.find((t) => t.id === a.theme)?.themeColor;
  if (color) {
    document
      .querySelectorAll('meta[name="theme-color"]')
      .forEach((m) => m.setAttribute("content", color));
  }
}

export function saveAppearance(next: Appearance) {
  try {
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify(next));
  } catch {}
  applyAppearance(next);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribeAppearance(cb: () => void) {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== APPEARANCE_KEY) return;
    applyAppearance(readAppearance());
    cb();
  };
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb);
    window.removeEventListener("storage", onStorage);
  };
}

// Runs in <head> before first paint so a saved theme never flashes Graphite.
export const APPEARANCE_BOOT_SCRIPT = `(function(){try{var a=JSON.parse(localStorage.getItem('${APPEARANCE_KEY}')||'{}'),d=document.documentElement,t=${JSON.stringify(
  THEMES.map((t) => t.id),
)};d.dataset.theme=t.indexOf(a.theme)>-1?a.theme:'graphite';if(a.contrast==='high')d.dataset.contrast='high';if(a.motion==='reduced')d.dataset.motion='reduced';if(${JSON.stringify(
  TEXT_SIZES.map((t) => t.id).filter((id) => id !== "default"),
)}.indexOf(a.textSize)>-1)d.dataset.textSize=a.textSize;}catch(e){document.documentElement.dataset.theme='graphite';}})();`;
